import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Context Providers
import { AuthProvider } from './contexts/AuthContext';

// Layout Components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

// Public Pages
import HomePage from './pages/public/HomePage';
import SportsPage from './pages/public/SportsPage';
import SportDetailPage from './pages/public/SportDetailPage';
import NewsPage from './pages/public/NewsPage';
import NewsDetailPage from './pages/public/NewsDetailPage';
import EventsPage from './pages/public/EventsPage';
import EventDetailPage from './pages/public/EventDetailPage';
import MediaPage from './pages/public/MediaPage';
import ContactPage from './pages/public/ContactPage';
import RegistrationPage from './pages/public/RegistrationPage';
import FamilyResourcesPage from './pages/public/FamilyResourcesPage';
import HandbookPage from './pages/public/HandbookPage';
import FeesPage from './pages/public/FeesPage';
import ParentInfoPage from './pages/public/ParentInfoPage';
import AcademicPage from './pages/public/AcademicPage';
import ImportantDatesPage from './pages/public/ImportantDatesPage';
import FAQPage from './pages/public/FAQPage';
import DonationsPage from './pages/public/DonationsPage';
import FacilitiesPage from './pages/public/FacilitiesPage';
import SafetyPage from './pages/public/SafetyPage';
import CoachResourcesPage from './pages/public/CoachResourcesPage';
import DownloadsPage from './pages/public/DownloadsPage';
import SearchPage from './pages/public/SearchPage';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';

// Admin Pages
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminSports from './pages/admin/AdminSports';
import SportsManagement from './pages/admin/SportsManagement';
import RosterManagement from './pages/admin/RosterManagement';
import ScheduleManagement from './pages/admin/ScheduleManagement';
import ResultsManagement from './pages/admin/ResultsManagement';
import AdminNews from './pages/admin/AdminNews';
import AdminEvents from './pages/admin/AdminEvents';
import AdminMedia from './pages/admin/AdminMedia';
import UsersPage from './pages/admin/UsersPage';
import HeroImagesPage from './pages/admin/HeroImagesPage';
import SiteSettingsPage from './pages/admin/SiteSettingsPage';
import CalendarManagement from './pages/admin/CalendarManagement';

import DonationManagement from './pages/admin/DonationManagement';
import MediaManagement from './pages/admin/MediaManagement';
import FacilityManagement from './pages/admin/FacilityManagement';
import SafetyManagement from './pages/admin/SafetyManagement';
import CoachResourcesManagement from './pages/admin/CoachResourcesManagement';
import AdminDocuments from './pages/admin/AdminDocuments';
import ActivityPage from './pages/admin/ActivityPage';
import BackupManagement from './pages/admin/BackupManagement';

// Protected Route Component
import ProtectedRoute from './components/auth/ProtectedRoute';

// Error Pages
import NotFoundPage from './pages/NotFoundPage';

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="min-h-screen bg-gray-50 flex flex-col safe-area-top safe-area-bottom">
          <Routes>
            {/* Admin Routes */}
            <Route path="/admin/*" element={
              <ProtectedRoute requiredRole="admin">
                <AdminLayout>
                  <Routes>
                    <Route index element={<AdminDashboard />} />
                    <Route path="activity" element={<ActivityPage />} />
                    <Route path="sports" element={<SportsManagement />} />
                    <Route path="sports/:sportId/roster" element={<RosterManagement />} />
                    <Route path="sports/:sportId/schedule" element={<ScheduleManagement />} />
                    <Route path="sports/:sportId/results" element={<ResultsManagement />} />
                    <Route path="sports-old" element={<AdminSports />} />
                    <Route path="news" element={<AdminNews />} />
                    <Route path="events" element={<AdminEvents />} />
                    <Route path="calendar" element={<CalendarManagement />} />
                    <Route path="documents" element={<AdminDocuments />} />


                    <Route path="donations" element={<DonationManagement />} />
                    <Route path="facilities" element={<FacilityManagement />} />
                    <Route path="safety" element={<SafetyManagement />} />
                    <Route path="coach-resources" element={<CoachResourcesManagement />} />
                    <Route path="hero-images" element={<HeroImagesPage />} />
                    <Route path="media" element={<MediaManagement />} />
                    <Route path="users" element={<UsersPage />} />
                    <Route path="backup" element={<BackupManagement />} />
                    <Route path="settings" element={<SiteSettingsPage />} />
                  </Routes>
                </AdminLayout>
              </ProtectedRoute>
            } />
            
            {/* Auth Routes */}
            <Route path="/login" element={<LoginPage />} />
            
            {/* Public Routes with Header/Footer */}
            <Route path="/*" element={
              <>
                <Header />
                <main className="flex-grow">
                  <Routes>
                    <Route index element={<HomePage />} />
                    <Route path="sports" element={<SportsPage />} />
                    <Route path="sports/:slug" element={<SportDetailPage />} />
                    <Route path="news" element={<NewsPage />} />
                    <Route path="news/:slug" element={<NewsDetailPage />} />
                    <Route path="events" element={<EventsPage />} />
                    <Route path="events/:id" element={<EventDetailPage />} />
                    <Route path="media" element={<MediaPage />} />
                    <Route path="family-resources" element={<FamilyResourcesPage />} />
                    <Route path="family-resources/handbook" element={<HandbookPage />} />
                    <Route path="family-resources/fees" element={<FeesPage />} />
                    <Route path="family-resources/parent-info" element={<ParentInfoPage />} />
                    <Route path="family-resources/academic" element={<AcademicPage />} />
                    <Route path="family-resources/important-dates" element={<ImportantDatesPage />} />
                    <Route path="family-resources/faq" element={<FAQPage />} />
                    <Route path="donations" element={<DonationsPage />} />
                    <Route path="facilities" element={<FacilitiesPage />} />
                    <Route path="safety" element={<SafetyPage />} />
                    <Route path="coach-resources" element={<CoachResourcesPage />} />
                    <Route path="downloads" element={<DownloadsPage />} />
                    <Route path="search" element={<SearchPage />} />
                    <Route path="contact" element={<ContactPage />} />
                    <Route path="register/:sportId" element={<RegistrationPage />} />
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </main>
                <Footer />
              </>
            } />
          </Routes>
          
          {/* Toast Notifications */}
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;